import * as React from 'react';
import Amplify, { CognitoUser } from '@aws-amplify/auth';
import {
  CognitoUserSession,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { Nullable } from '..';
// import { Redirect } from 'react-router';
// import { async } from 'q';

Amplify.configure({
  identityPoolId: 'us-east-1:2e4713a0-19c4-4ad1-ac1e-96f05e352720',
  region: 'us-east-1',
  userPoolId: 'us-east-1_gOtLS6qkI',
  userPoolWebClientId: '2jfn74pkt372akra0o8q1lbdvq'
  // mandatorySignIn: false,
  // authenticationFlowType: 'USER_PASSWORD_AUTH',
});

interface ISignUpParams {
  username: string;
  password: string;
  phone: string;
}

interface ILoginParams {
  username: string;
  password: string;
}

interface IForgotPasswordParams {
  username: string;
  password: string;
  code: string;
}

interface IVerifyAccountParams {
  username: string;
  code: string;
}

interface IUpdateUserClioParams {
  clioId: string;
  clioAccessToken: string;
  clioRefreshToken: string;
  clioTimestamp: string;
}

interface IUpdateUserAccountParams {
  firstName: string;
  lastName: string;
  email: string;
  lawFirm: string;
  jobTitle: string;
  streetAddress: string;
  officePhone: string;
}

interface IChangePasswordParams {
  oldpwd: string;
  newpwd: string;
}

interface IAuthContext {
  user: Nullable<CognitoUser>;
  session: Nullable<CognitoUserSession>;
  accessToken: Nullable<CognitoAccessToken>;
  refreshToken: Nullable<CognitoRefreshToken>;
  jwtToken: string;
  username: string;
  hasValidSession: boolean;
  requireVerification: boolean;
  usernameForVerification: string;
  resetNewPassword: boolean;

  login: (params: ILoginParams) => void;
  signup: (params: ISignUpParams) => void;
  forgotPassword: (step: number, params: IForgotPasswordParams)  => void;
  verifyAccount: (params: IVerifyAccountParams) => void;
  updateUserClio: (params: IUpdateUserClioParams) => void;
  updateUserAccount: (params: IUpdateUserAccountParams) => void;
  changePassword: (params: IChangePasswordParams) => void;
  logout: () => void;
  isAuthenticated: () => void;
  currentUserData: () => void;
}

interface IAuthState {
  user: Nullable<CognitoUser>;
  session: Nullable<CognitoUserSession>;
  accessToken: Nullable<CognitoAccessToken>;
  refreshToken: Nullable<CognitoRefreshToken>;
  jwtToken: string;
  username: string;
  hasValidSession: boolean;
  requireVerification: boolean;
  usernameForVerification: string;
  resetNewPassword: boolean;
}

const initialState: IAuthState = {
  user: null,
  session: null,
  accessToken: null,
  refreshToken: null,
  jwtToken: '',
  username: '',
  hasValidSession: false,
  requireVerification: false,
  usernameForVerification: '',
  resetNewPassword: false
};

export const AuthContext = React.createContext<Partial<IAuthContext>>({});
export const AuthConsumer = AuthContext.Consumer;

export class AuthProvider extends React.Component<any, any> {
  public state: IAuthState = initialState;

  public async componentDidMount() {
    try {
      /**
       * If AuthContextProvider mounts and it's not currently authenticated,
       * we will try to hydrate user data from localStorage.
       */

      const storage = await localStorage.getItem(
        'LawDroid.ATJ.Web.authContext',
      );

      console.log(storage);

      if (storage) {
        /**
         * localStorage has what we need. Parsing the JSON and setting the state.
         */
        const hydratedState: Partial<IAuthState> = await JSON.parse(storage);
        // console.log(hydratedState);
        await this.setState({ ...this.state, ...hydratedState });
        // console.log(this.state);
      }

      if (!this.state.hasValidSession) {
        /**
         * Try to fetch the latest from localStorage.
         */
        const user: CognitoUser = await Amplify.currentAuthenticatedUser();
        // console.log(user);
        
        const session: Nullable<CognitoUserSession> = await Amplify.currentSession();
        // console.log(session);

        const requiresVerification = !(user as any).attributes.email_verified;
        // console.log(requiresVerification);

        const refreshToken = session && session.getRefreshToken();

        const accessToken = session && session.getAccessToken();

        const jwtToken = session && session.getAccessToken().getJwtToken();
        console.log(jwtToken);

        const username = user['username'];

        console.log(user,username);

        this.setState({
          user,
          session,
          accessToken,
          jwtToken,
          username,
          refreshToken,
          hasValidSession: !!accessToken,
          requiresVerification,
          resetNewPassword: false
        });
      }
    } catch (error) {
      // console.log('AuthContext.componentDidMount', error);
      if (error.code === 'UserNotConfirmedException') {
        this.setState({ ...this.state, requireVerification: true,resetNewPassword: false });
      }

      // console.log(this.state);
      // throw new Error(error.message);
    }
  }

  public login = async ({
    username: usrName,
    password: usrPass,
  }: ILoginParams) => {
    try {
      /**
       * Check user authentication with server
       * stringify data from response and store in localStorage
       */

      usrName = usrName.toString().toLowerCase();
      this.setState({ usernameForVerification: usrName });

      const user: CognitoUser = await Amplify.signIn(usrName, usrPass);

      const session: Nullable<CognitoUserSession> = user.getSignInUserSession();
      const requiresVerification = !(user as any).attributes.email_verified;
      const usernameForVerification = requiresVerification ? usrName : '';
      const resetNewPassword= false;

      const accessToken = session && session.getAccessToken();
      const refreshToken = session && session.getRefreshToken();

      const jwtToken = session && session.getAccessToken().getJwtToken();

      const username = user['username'];

      this.setState({
        user,
        session,
        accessToken,
        refreshToken,
        jwtToken,
        username,
        hasValidSession: !!accessToken,
        requiresVerification,
        usernameForVerification,
        resetNewPassword: false
      });

      // console.log('STATE', this.state);

      const stateToStorage = JSON.stringify({
        requiresVerification,
        usernameForVerification,
        resetNewPassword
      });
      await localStorage.setItem(
        'LawDroid.ATJ.Web.authContext',
        stateToStorage,
      );
    } catch (error) {
      if (error.code === 'UserNotConfirmedException') {
        this.setState({ requireVerification: true, resetNewPassword: false });
        return;
      }
      // console.log('AuthContext.login', error);
      throw new Error(error.message);
    }
  };

  public updateUserClio = async ({ clioId, clioAccessToken, clioRefreshToken, clioTimestamp }: IUpdateUserClioParams) => {
    try {
      const userObj: CognitoUser = await Amplify.currentAuthenticatedUser();
      await Amplify.updateUserAttributes(userObj, {
              'custom:clioId': clioId,
              'custom:clioAccessToken': clioAccessToken,
              'custom:clioRefreshToken': clioRefreshToken,
              'custom:clioTimestamp': clioTimestamp
            });

      localStorage.setItem(userObj['keyPrefix']+'.'+userObj['username']+'.userData', '');
      
      const user: CognitoUser = await Amplify.currentAuthenticatedUser();
      const session: Nullable<
        CognitoUserSession
      > = await Amplify.currentSession();

      const accessToken = session && session.getAccessToken();
      const refreshToken = session && session.getRefreshToken();
      const jwtToken = session && session.getAccessToken().getJwtToken();
      const username = user['username'];

      this.setState({
         ...this.state,
         user,
         session,
         accessToken,
         refreshToken,
         jwtToken,
         username
      });
    } catch (error) {
      throw new Error(error.message);
    }
  };

  public updateUserAccount = async ({ firstName, lastName, email, lawFirm, jobTitle, streetAddress, officePhone }: IUpdateUserAccountParams) => {
    try {
      const userObj: CognitoUser = await Amplify.currentAuthenticatedUser();      
      await Amplify.updateUserAttributes(userObj, {
              'custom:firstName': firstName,
              'custom:lastName': lastName,
              'email': email,
              'custom:formCompleted': "true",
              'custom:lawFirm': lawFirm,
              'custom:jobTitle': jobTitle,
              'custom:streetAddress': streetAddress,
              'custom:mobilePhone': officePhone,
              'custom:officePhone': officePhone,
              'custom:timeZone': "America/Vancouver"
            });

      localStorage.setItem(userObj['keyPrefix']+'.'+userObj['username']+'.userData', '');
      
      const user: CognitoUser = await Amplify.currentAuthenticatedUser();
      const session: Nullable<
        CognitoUserSession
      > = await Amplify.currentSession();

      const accessToken = session && session.getAccessToken();
      const refreshToken = session && session.getRefreshToken();
      const jwtToken = session && session.getAccessToken().getJwtToken();
      const username = user['username'];


      this.setState({
         ...this.state,
         user,
         session,
         accessToken,
         refreshToken,
         jwtToken,
         username
      });
    } catch (error) {
      throw new Error(error.message);
    }
  };

  public changePassword = async ({ oldpwd, newpwd }: IChangePasswordParams) => {
    try {
      const userObj: CognitoUser = await Amplify.currentAuthenticatedUser();
      
      await Amplify.changePassword(userObj, oldpwd, newpwd);

      const user: CognitoUser = await Amplify.currentAuthenticatedUser();
      const session: Nullable<
        CognitoUserSession
      > = await Amplify.currentSession();

      const accessToken = session && session.getAccessToken();
      const refreshToken = session && session.getRefreshToken();
      const jwtToken = session && session.getAccessToken().getJwtToken();
      const username = user['username'];


      this.setState({
         ...this.state,
         user: user,
         username,
         jwtToken,
         accessToken,
         refreshToken
      });
    } catch (error) {
      // console.log('AuthContext.updateUserAccount', error);
      throw new Error(error.message);
    }
  };

  public verifyAccount = async ({ username, code }: IVerifyAccountParams) => {
    try {
      await Amplify.confirmSignUp(username.toString().toLowerCase(), code);

      this.setState({
         ...this.state,
        requiresVerification: false,
        usernameForVerification: '',
        resetNewPassword: false
      });
    } catch (error) {
      console.log(error);
      throw new Error(error.message);
    }
  };

  public signup = async ({ username, password, phone }: ISignUpParams) => {
    try {
      username = username.toString().toLowerCase();
      const user = await Amplify.signUp({username, password,
        attributes: {
          'email': username.toString().toLowerCase(),
          'custom:officePhone': phone,
          'custom:mobilePhone': phone
        }
      });

      this.setState({
        ...this.state,
        user: user.user,
        requireVerification: true,
        usernameForVerification: username,
      });

      const stateToStorage = JSON.stringify({
        requiresVerification: true,
        usernameForVerification: username,
        resetNewPassword: false
      });
      await localStorage.setItem(
        'LawDroid.ATJ.Web.authContext',
        stateToStorage,
      );

      // console.log(user);
    } catch (error) {
      if (error.code === 'UserNotConfirmedException') {
        this.setState({ requireVerification: true, resetNewPassword: false });
        return;
      }

      // console.log('AuthContext.signUp', error);
      throw new Error(error.message);
    }
  };

  public logout = async () => {
    this.state.user && (await this.state.user.signOut());
    await localStorage.removeItem('LawDroid.ATJ.Web.authContext');
    this.setState(initialState);
    window.location.href = '/';
  };

  public forgotPassword = async (step: number, params: IForgotPasswordParams) => {
    try {
      // console.log('AuthContext.forgotPassword', step, params);

      if (step === 1) {
        const { username } = params;
        await Amplify.forgotPassword(username.toString().toLowerCase());

        const stateToStorage = JSON.stringify({
          requiresVerification: false,
          usernameForVerification: username,
          resetNewPassword: true
        });
        await localStorage.setItem(
          'LawDroid.ATJ.Web.authContext',
          stateToStorage,
        );

        return true;
      }

      if (step === 2) {
        const { username, password, code } = params;
        await Amplify.forgotPasswordSubmit(username.toString().toLowerCase(), code, password);

        const stateToStorage = JSON.stringify({
          requiresVerification: false,
          usernameForVerification: username,
          resetNewPassword: false
        });
        await localStorage.setItem(
          'LawDroid.ATJ.Web.authContext',
          stateToStorage,
        );

        return true;
      }
    } catch (error) {
      console.log('AuthContext.forgotPassword', error);
      this.setState({ requireVerification: false, resetNewPassword: false });
      throw new Error(error.message);
    }
  };

  public isAuthenticated = () => {
    return !!this.state.session && this.state.session.isValid();
  };

  public currentUserData = async () => {
    const user: CognitoUser = await Amplify.currentAuthenticatedUser();
    const session: Nullable<
      CognitoUserSession
    > = await Amplify.currentSession();

    const accessToken = session && session.getAccessToken();
    const refreshToken = session && session.getRefreshToken();
    const jwtToken = session && session.getAccessToken().getJwtToken();
    const username = user['username'];


    // console.log(user);
    this.setState({
      user : user,
      session: session,
      accessToken: accessToken,
      jwtToken: jwtToken,
      username: username,
      refreshToken: refreshToken,
      hasValidSession: true,
      requiresVerification: false,
      usernameForVerification: '',
      resetNewPassword: false
    });
  };

  public render() {
    const {
      user,
      accessToken,
      refreshToken,
      jwtToken,
      username,
      session,
      hasValidSession,
      requireVerification,
      usernameForVerification,
      resetNewPassword
    } = this.state;

    return (
      <AuthContext.Provider
        value={{
          user,
          accessToken,
          refreshToken,
          jwtToken,
          username,
          session,
          hasValidSession,
          requireVerification,
          usernameForVerification,
          resetNewPassword,

          login: this.login,
          signup: this.signup,
          logout: this.logout,
          forgotPassword: this.forgotPassword,
          verifyAccount: this.verifyAccount,
          isAuthenticated: this.isAuthenticated,
          currentUserData: this.currentUserData,
          updateUserAccount: this.updateUserAccount,
          updateUserClio: this.updateUserClio,
          changePassword: this.changePassword
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}
